import React, { useEffect, useState } from 'react';
import * as cloudinaryAPI from '../../../api/cloudinaryAPI';
import OpportunityButton from '../organizations/opportunities/OpportunityButton';
import * as organizationsAPI from '../../../api/organizationsAPI';
import { StatusPopup } from './StatusPopup';
import { formatCloudinaryThumbnailURLToBeFullSize } from '../../../api/cloudinaryAPI';

/**
 * Component for the PhotoViewer
 * @param data the data to be referenced (in gallery's case, orgData)
 * @param setData the function to update data (in gallery's case, setOrgData)
 * @param show whether to show the photoviewer or not
 * @param setShow function to set show from above
 * @param startingIndex the index belonging to the starting image to be displayed
 * @param currentUserCanEdit whether or not the current user can edit
 */
export const PhotoViewer = ({
	data,
	setData,
	show,
	setShow,
	startingIndex,
	currentUserCanEdit,
}) => {
	// If not meant to be shown yet, don't show anything
	if (!show) {
		return;
	}

	/**
	 * ---- Variables/States ----
	 * activeFileIndex: Keeps track of the current active file index (ie. the one to be displayed in the middle of the screen)
	 * activeFileCase: Keeps track of the current file's case type (1 - supported image, 2 - unsupported image, 3 - video, 0 - anything else)
	 * This is also needed for displaying the file correctly
	 * statusPopupObject: Keeps track of the status popup banner component data
	 * showStatusPopupObject: Responsible for showing and hiding the status popup banner component. True for show, false for hide.
	 */
	const [activeFileIndex, setActiveFileIndex] = useState(startingIndex);
	const [activeFileCase, setActiveFileCase] = useState(0);
	const [statusPopupObject, setStatusPopupObject] = useState({});
	const [showStatusPopupObject, setShowStatusPopupObject] = useState(false);

	/**
	 * On component mount, mark the starting index as active, and add an event listener that will be responsible for key presses
	 */
	useEffect(() => {
		document
			.getElementById('thumbnail-' + startingIndex)
			.classList.add('active');
		document.addEventListener('keydown', handleKeyDown);
	}, []);

	/**
	 * When the active image index is changed, refresh the event listeners, and then mark the new active image as active,
	 * while scrolling it into view if needed. Also resets the active asset case.
	 */
	useEffect(() => {
		document.removeEventListener('keydown', handleKeyDown);
		document.addEventListener('keydown', handleKeyDown);
		document.getElementById('thumbnail-' + activeFileIndex).scrollIntoView({
			behavior: 'smooth',
			block: 'center',
			inline: 'nearest',
		});
		setActiveFileCase(
			cloudinaryAPI.checkFileType(data.assets[activeFileIndex])
		);
	}, [activeFileIndex]);

	/**
	 * Handles hiding the PhotoViewer
	 */
	const handleClose = () => {
		setShow(false);
	};

	/**
	 * Handles key presses, for moving around the photo viewer
	 * @param e the key down event
	 */
	const handleKeyDown = (e) => {
		// Right button
		if (e.keyCode === 39) {
			incrementFileIndex();
		}
		// Left button
		else if (e.keyCode === 37) {
			decrementFileIndex();
		}
		// Esc button
		else if (e.keyCode === 27) {
			handleClose();
		}
	};

	/**
	 * Decreases the image index or moves it to the next possible index to the image's left.
	 */
	const decrementFileIndex = () => {
		changeFileIndex(
			(activeFileIndex - 1 + data.assets.length) % data.assets.length
		);
	};

	/**
	 * Increases the image index or moves it to the next possible index to the image's right.
	 */
	const incrementFileIndex = () => {
		changeFileIndex((activeFileIndex + 1) % data.assets.length);
	};

	/**
	 * Changes the current image index to the specified value, and resets which thumbnails are active
	 * @param newIndex the next index to set active
	 */
	const changeFileIndex = (newIndex) => {
		document
			.getElementById('thumbnail-' + activeFileIndex)
			.classList.remove('active');
		document
			.getElementById('thumbnail-' + newIndex)
			.classList.add('active');
		document.removeEventListener('keydown', handleKeyDown);
		setActiveFileIndex(newIndex);
	};

	/**
	 * Changes the current active index
	 * @param index the next index to set active
	 */
	const changeActiveFileIndex = (index) => {
		document
			.getElementById('thumbnail-' + activeFileIndex)
			.classList.remove('active');
		setActiveFileIndex(index);
		document.getElementById('thumbnail-' + index).classList.add('active');
	};

	/**
	 * Deletes the current file
	 */
	const deleteFile = async () => {
		let index = activeFileIndex;
		setShowStatusPopupObject(false);

		await organizationsAPI.deleteOrganizationAsset(
			data.id,
			data.assets[index]
		);

		data.assets.splice(index, 1);
		setData(data);

		const tempShowStatusObject = {
			type: 'success',
			text: 'File Deleted',
		};
		if (data.assets.length === 0) {
			handleClose();
		} else {
			incrementFileIndex();
		}

		setStatusPopupObject(tempShowStatusObject);
		setShowStatusPopupObject(true);
	};

	return (
		<div>
			<div className="background-filter" onClick={handleClose} />
			<div className="photo-viewer" id="temp">
				{showStatusPopupObject ? (
					<StatusPopup
						statusPopupObject={statusPopupObject}
						setShowStatusPopup={setShowStatusPopupObject}
					/>
				) : (
					''
				)}

				<i
					id="media-upload-close-button"
					className="fas fa-times float-right"
					onClick={handleClose}
				/>

				{/*The center image, along with the left and right arrows*/}
				<div className="center-box">
					<i
						className="fas fa-chevron-left"
						onClick={() => decrementFileIndex()}
					/>
					{activeFileCase === 1 ? (
						<img
							className="main-asset"
							src={data.assets[activeFileIndex].secure_url}
							alt=""
						/>
					) : activeFileCase === 2 ? (
						<img
							src={cloudinaryAPI.formatCloudinaryThumbnailURLToBeFullSize(
								data.assets[activeFileIndex].thumbnail_url
							)}
							alt=""
							className="main-asset"
						/>
					) : activeFileCase === 3 ? (
						<video className="main-asset" controls width="90%">
							<source
								src={data.assets[activeFileIndex].secure_url}
							/>
						</video>
					) : (
						<div className="main-asset">
							<p>
								Cannot retrieve this file's thumbnail, try
								opening it with the button below
							</p>
						</div>
					)}

					<i
						className="fas fa-chevron-right"
						onClick={() => incrementFileIndex()}
					/>
				</div>

				{/*The open file and delete buttons*/}
				<div className="spacer">
					<div
						className="primary-button"
						onClick={() => {
							window.open(
								data.assets[activeFileIndex].secure_url
							);
						}}
					>
						<p>
							Open{' '}
							{activeFileCase === 1
								? 'Image'
								: activeFileCase === 2
								? 'File'
								: activeFileCase === 3
								? 'Video'
								: 'File'}
						</p>
					</div>
					{currentUserCanEdit ? (
						<OpportunityButton
							text="Delete"
							className="delete-button"
							handleSubmit={deleteFile}
							isBackLink={false}
							leftButtonText="Cancel"
							rightButtonText="Delete"
							headerText="Do you want to delete this file? It will be lost forever."
						/>
					) : (
						<></>
					)}
				</div>

				{/*The bottom carousel for thumbnails*/}
				<div className="bottom-box">
					<h1>Your Images</h1>
					{data.assets.map((asset, index) => {
						let assetCase = cloudinaryAPI.checkFileType(asset);
						if (
							assetCase === 1 ||
							assetCase === 2 ||
							assetCase === 3
						) {
							return (
								<div
									className="image-thumbnail"
									key={index}
									onClick={() => changeActiveFileIndex(index)}
								>
									<img
										id={'thumbnail-' + index}
										className={'temp'}
										src={formatCloudinaryThumbnailURLToBeFullSize(
											asset.thumbnail_url
										)}
										alt={'image'}
									/>
								</div>
							);
						} else {
							return (
								<div
									className="secondary-button"
									id={'thumbnail-' + index}
									key={index}
									onClick={() => changeActiveFileIndex(index)}
								>
									<p>Unsupported File Format</p>
								</div>
							);
						}
					})}
				</div>
			</div>
		</div>
	);
};
